import { supabase } from '../../../lib/supabase';
import type { 
  Medicine, 
  BatchStock, 
  MedicineFormData, 
  BatchStockFormData, 
  StockSummary,
  Supplier,
  SupplierFormData 
} from '../types';

const isSupabasePharmacyEnabled = () => {
  return import.meta.env?.VITE_USE_SUPABASE_PHARMACY === 'true';
};

function productToMedicine(product: any): Medicine {
  return {
    id: product.id,
    name: product.name,
    genericName: product.generic_name,
    brandName: product.brand_name,
    strength: product.strength || '',
    form: product.form || 'other',
    route: product.route,
    manufacturer: product.manufacturer,
    reorderLevel: product.reorder_level || 10,
    status: product.status || 'active',
    createdAt: product.created_at,
    updatedAt: product.updated_at
  };
}

function medicineToProduct(medicine: MedicineFormData) {
  return {
    name: medicine.name,
    generic_name: medicine.genericName,
    brand_name: medicine.brandName,
    strength: medicine.strength,
    form: medicine.form,
    route: medicine.route,
    manufacturer: medicine.manufacturer,
    reorder_level: medicine.reorderLevel,
    status: medicine.status,
    category: medicine.form || 'other',
    unit: 'piece',
    price: 0
  };
}

function batchToBatchStock(batch: any): BatchStock {
  return {
    id: batch.id,
    medicineId: batch.product_id,
    batchNo: batch.batch_number,
    expiryDate: batch.expiry_date,
    quantityOnHand: batch.current_quantity,
    location: batch.location,
    unitPrice: batch.selling_price,
    mrp: batch.mrp,
    createdAt: batch.created_at,
    updatedAt: batch.updated_at
  };
}

function batchStockToBatch(stock: BatchStockFormData) {
  return {
    product_id: stock.medicineId,
    batch_number: stock.batchNo,
    expiry_date: stock.expiryDate,
    current_quantity: stock.quantityOnHand,
    initial_quantity: stock.quantityOnHand,
    location: stock.location,
    selling_price: stock.unitPrice,
    purchase_price: stock.unitPrice * 0.7,
    mrp: stock.mrp
  };
}

export const supabaseMedicineService = {
  async getAll(): Promise<Medicine[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('status', 'active')
      .order('name');
    
    if (error) throw error;
    return (data || []).map(productToMedicine);
  },

  async checkDuplicate(name: string, excludeId?: string): Promise<boolean> {
    let query = supabase
      .from('products')
      .select('id')
      .ilike('name', name);
    
    if (excludeId) {
      query = query.neq('id', excludeId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return (data || []).length > 0;
  },

  async getById(id: string): Promise<Medicine | null> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data ? productToMedicine(data) : null;
  },

  async create(medicine: MedicineFormData): Promise<Medicine> {
    const { data, error } = await supabase
      .from('products')
      .insert([medicineToProduct(medicine)])
      .select()
      .single();
    
    if (error) throw error;
    return productToMedicine(data);
  },

  async update(id: string, medicine: Partial<MedicineFormData>): Promise<Medicine | null> {
    const updates: any = {};
    if (medicine.name) updates.name = medicine.name;
    if (medicine.genericName !== undefined) updates.generic_name = medicine.genericName;
    if (medicine.brandName !== undefined) updates.brand_name = medicine.brandName;
    if (medicine.strength !== undefined) updates.strength = medicine.strength;
    if (medicine.form !== undefined) updates.form = medicine.form;
    if (medicine.route !== undefined) updates.route = medicine.route;
    if (medicine.manufacturer !== undefined) updates.manufacturer = medicine.manufacturer;
    if (medicine.reorderLevel !== undefined) updates.reorder_level = medicine.reorderLevel;
    if (medicine.status !== undefined) updates.status = medicine.status;
    
    const { data, error } = await supabase
      .from('products')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data ? productToMedicine(data) : null;
  },

  async search(query: string): Promise<Medicine[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .or(`name.ilike.%${query}%,generic_name.ilike.%${query}%,brand_name.ilike.%${query}%`)
      .eq('status', 'active')
      .order('name');
    
    if (error) throw error;
    return (data || []).map(productToMedicine);
  },

  async getActiveOnly(): Promise<Medicine[]> {
    return this.getAll();
  }
};

export const supabaseStockService = {
  async getAll(): Promise<BatchStock[]> {
    const { data, error } = await supabase
      .from('product_batches')
      .select('*')
      .order('expiry_date');
    
    if (error) throw error;
    return (data || []).map(batchToBatchStock);
  },

  async getById(id: string): Promise<BatchStock | null> {
    const { data, error } = await supabase
      .from('product_batches')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data ? batchToBatchStock(data) : null;
  },

  async getByMedicine(medicineId: string): Promise<BatchStock[]> {
    const { data, error } = await supabase
      .from('product_batches')
      .select('*')
      .eq('product_id', medicineId)
      .order('expiry_date');
    
    if (error) throw error;
    return (data || []).map(batchToBatchStock);
  },

  async upsert(stock: BatchStockFormData): Promise<BatchStock> {
    const batchData = batchStockToBatch(stock);
    
    const { data, error } = await supabase
      .from('product_batches')
      .upsert([batchData], { onConflict: 'product_id,batch_number' })
      .select()
      .single();
    
    if (error) throw error;
    return batchToBatchStock(data);
  },

  async getSummary(): Promise<StockSummary[]> {
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id, name, reorder_level')
      .eq('status', 'active');
    
    if (productsError) throw productsError;

    const { data: batches, error: batchesError } = await supabase
      .from('product_batches')
      .select('product_id, current_quantity, expiry_date')
      .gt('current_quantity', 0);
    
    if (batchesError) throw batchesError;

    const summary: StockSummary[] = (products || []).map(product => {
      const productBatches = (batches || []).filter(b => b.product_id === product.id);
      const totalStock = productBatches.reduce((sum, batch) => sum + batch.current_quantity, 0);
      const nearestExpiry = productBatches
        .sort((a, b) => new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime())[0]?.expiry_date || '';

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      return {
        medicineId: product.id,
        medicineName: product.name,
        totalStock,
        nearestExpiry,
        reorderLevel: product.reorder_level || 10,
        isLowStock: totalStock <= (product.reorder_level || 10),
        isNearExpiry: nearestExpiry ? new Date(nearestExpiry) <= thirtyDaysFromNow : false,
      };
    });

    return summary;
  },

  async getLowStock(): Promise<StockSummary[]> {
    const summary = await this.getSummary();
    return summary.filter(s => s.isLowStock);
  },

  async getNearExpiry(): Promise<StockSummary[]> {
    const summary = await this.getSummary();
    return summary.filter(s => s.isNearExpiry);
  },

  async getAvailableBatches(medicineId: string): Promise<BatchStock[]> {
    const now = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('product_batches')
      .select('*')
      .eq('product_id', medicineId)
      .gt('current_quantity', 0)
      .gt('expiry_date', now)
      .order('expiry_date');
    
    if (error) throw error;
    return (data || []).map(batchToBatchStock);
  },

  async getBatchesWithMedicine(): Promise<any[]> {
    const { data, error } = await supabase
      .from('product_batches')
      .select(`
        *,
        medicine:products(*)
      `)
      .gt('current_quantity', 0)
      .order('expiry_date');
    
    if (error) throw error;
    
    return (data || []).map(batch => ({
      ...batchToBatchStock(batch),
      medicine: batch.medicine ? productToMedicine(batch.medicine) : null
    }));
  }
};

export const supabaseSupplierService = {
  async getAll(): Promise<Supplier[]> {
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return (data || []).map(s => ({
      id: s.id,
      name: s.name,
      contactPerson: s.contact_person,
      phone: s.phone,
      email: s.email,
      address: s.address,
      taxId: s.tax_id,
      status: s.status,
      createdAt: s.created_at,
      updatedAt: s.updated_at
    }));
  },

  async create(supplier: SupplierFormData): Promise<Supplier> {
    const { data, error } = await supabase
      .from('suppliers')
      .insert([{
        name: supplier.name,
        contact_person: supplier.contactPerson,
        phone: supplier.phone,
        email: supplier.email,
        address: supplier.address,
        tax_id: supplier.taxId,
        status: supplier.status
      }])
      .select()
      .single();
    
    if (error) throw error;
    return {
      id: data.id,
      name: data.name,
      contactPerson: data.contact_person,
      phone: data.phone,
      email: data.email,
      address: data.address,
      taxId: data.tax_id,
      status: data.status,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  async update(id: string, supplier: Partial<SupplierFormData>): Promise<Supplier | null> {
    const updates: any = {};
    if (supplier.name) updates.name = supplier.name;
    if (supplier.contactPerson !== undefined) updates.contact_person = supplier.contactPerson;
    if (supplier.phone !== undefined) updates.phone = supplier.phone;
    if (supplier.email !== undefined) updates.email = supplier.email;
    if (supplier.address !== undefined) updates.address = supplier.address;
    if (supplier.taxId !== undefined) updates.tax_id = supplier.taxId;
    if (supplier.status) updates.status = supplier.status;
    
    const { data, error } = await supabase
      .from('suppliers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data ? {
      id: data.id,
      name: data.name,
      contactPerson: data.contact_person,
      phone: data.phone,
      email: data.email,
      address: data.address,
      taxId: data.tax_id,
      status: data.status,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    } : null;
  }
};

export const createMedicineService = () => {
  if (isSupabasePharmacyEnabled()) {
    return supabaseMedicineService;
  }
  return require('./medicines').medicineService;
};

export const createStockService = () => {
  if (isSupabasePharmacyEnabled()) {
    return supabaseStockService;
  }
  return require('./stock').stockService;
};

export const supabaseRackService = {
  async getAll(): Promise<any[]> {
    const { data, error } = await supabase
      .from('pharmacy_racks')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return (data || []).map(r => ({
      id: r.id,
      name: r.name,
      location: r.location,
      capacity: r.capacity,
      notes: r.notes,
      createdAt: r.created_at,
      updatedAt: r.updated_at
    }));
  }
};

export const createSupplierService = () => {
  if (isSupabasePharmacyEnabled()) {
    return supabaseSupplierService;
  }
  return require('./suppliers').supplierService;
};

export const createRackService = () => {
  if (isSupabasePharmacyEnabled()) {
    return supabaseRackService;
  }
  return { getAll: async () => [] };
};
